/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import { defineMessages, useIntl } from 'react-intl-next';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { PublicLinkPageQuery } from '../graphql/PublicLinkPageQuery.graphql.ts';

import type {
	usePublicLinkDisablePageMutationPublicLinkDisablePageMutation as usePublicLinkDisablePageMutationPublicLinkDisablePageMutation$data,
	usePublicLinkDisablePageMutationPublicLinkDisablePageMutationVariables as usePublicLinkDisablePageMutationPublicLinkDisablePageMutation$variables,
} from './__types__/usePublicLinkDisablePageMutationPublicLinkDisablePageMutation.ts';

const i18n = defineMessages({
	success: {
		id: 'public-links.usePublicLinkDisablePageMutation.success.title',
		defaultMessage: 'Public link is now off for this content.',
		description: 'Message shown when the public link is successfully turned off.',
	},
	errorTitle: {
		id: 'public-links.usePublicLinkDisablePageMutation.error.title',
		defaultMessage: 'We couldn’t turn off the public link.',
		description: 'Message shown when there is an error while trying to turn off the public link.',
	},
	errorBody: {
		id: 'public-links.usePublicLinkDisablePageMutation.error.body',
		defaultMessage:
			'Open the Share dialog to check the current status, or to turn off the public link.',
		description: 'Message shown when there is an error while trying to turn off the public link.',
	},
});

export const usePublicLinkDisablePageMutation = ({
	pageId,
	onCompleted,
	onError,
	flags,
}: {
	pageId: string;
	onCompleted: () => void;
	onError: (error) => void;
	flags: FlagsStateContainer;
}) => {
	const { formatMessage } = useIntl();
	const [disablePublicLinkMutation] = useMutation<
		usePublicLinkDisablePageMutationPublicLinkDisablePageMutation$data,
		usePublicLinkDisablePageMutationPublicLinkDisablePageMutation$variables
	>(
		graphql`
			mutation usePublicLinkDisablePageMutationPublicLinkDisablePageMutation($pageId: ID!) {
				disablePublicLinkForPage(pageId: $pageId) {
					pageId
				}
			}
		`,
		{
			refetchQueries: [
				{
					query: PublicLinkPageQuery,
					variables: { pageId },
				},
			],
			onCompleted: () => {
				void flags.showFlag({
					id: 'revokeFromRestrictionsFlagId',
					title: formatMessage(i18n.success),
					type: 'success-circle',
					close: 'auto',
				});
				onCompleted();
			},
			onError: (error) => {
				void flags.showFlag({
					id: 'revokeFromRestrictionsErrorFlagId',
					title: formatMessage(i18n.errorTitle),
					description: formatMessage(i18n.errorBody),
					type: 'error',
					close: 'auto',
				});
				onError(error);
			},
		},
	);

	const disablePublicLinkForPage =
		(pageId: usePublicLinkDisablePageMutationPublicLinkDisablePageMutation$variables['pageId']) =>
		() =>
			disablePublicLinkMutation({
				variables: { pageId },
			});

	return {
		disablePublicLinkForPage: disablePublicLinkForPage(pageId),
	};
};
